import { isDesktop, windowControls } from "@shared/lib/desktop";
import { Hint } from "@shared/ui/bits";
import type { ConnectionState } from "@shared/realtime";
import { Mark, Word } from "@shared/ui/Brand";
import { ServerTile } from "@entities/server";
import { IconClose, IconHelp, IconInbox, IconMaximize, IconMinimize } from "@shared/ui/icons";

const CONNECTION: Record<ConnectionState, { label: string; bad: boolean }> = {
  live: { label: "соединение", bad: false },
  connecting: { label: "подключаюсь", bad: false },
  lost: { label: "связи нет", bad: true },
};

/** «Почта» в полосе окна — как в Discord: значок справа, счётчик упоминаний на нём. */
export interface ChromeInbox {
  count: number;
  open: () => void;
}

/** Где мы — посреди полосы окна, как в Discord: сервер со своим значком или личные сообщения. */
export type ChromePlace =
  | { kind: "server"; id: string; name: string; iconId: string | null }
  | { kind: "directs" };

/**
 * Строка заголовка.
 *
 * В десктопной сборке она заменяет системную: перетаскивание идёт по пустому
 * месту, кнопки окна свои. В браузере кнопок нет — там окном распоряжается
 * браузер, и рисовать чужие кнопки было бы обманом.
 */
export function Titlebar({
  subtitle,
  connection,
  inbox = null,
  place = null,
}: {
  subtitle: string;
  connection?: ConnectionState;
  /** «Почта» — у рабочего экрана; у входа её нет. */
  inbox?: ChromeInbox | null;
  /** Где мы — по центру полосы; нет — слева подпись, как на входе. */
  place?: ChromePlace | null;
}) {
  const status = connection ? CONNECTION[connection] : null;

  return (
    <header className="titlebar">
      <span className="titlebar-brand">
        <Mark size="bar" />
        <Word />
      </span>
      {place ? (
        // По центру, как в Discord: сервер — со своим значком, личные сообщения — со знаком Airel.
        <span className="titlebar-place">
          {place.kind === "server" ? (
            <ServerTile name={place.name} id={place.id} iconId={place.iconId} size={16} />
          ) : (
            <Mark size="bar" />
          )}
          <span className="ellipsis">{place.kind === "server" ? place.name : "Личные сообщения"}</span>
          {subtitle.endsWith("режим стримера") && <span className="titlebar-tag">режим стримера</span>}
        </span>
      ) : (
        <>
          <span className="titlebar-sep" />
          <span className="ellipsis" style={{ maxWidth: 320 }}>
            {subtitle}
          </span>
        </>
      )}

      <span className="grow drag" style={{ height: "100%" }} />

      {/*
        Состояние связи — одной точкой, без слова.

        Слово «соединение» ничего не сообщало: связь есть почти всегда, и
        подпись при ней — постоянный шум в полосе, которая и без того держит
        путь к каналу. Точка на том же месте отвечает на тот же вопрос и
        молчит, пока всё хорошо.

        Потерянную связь словами объясняет не она, а полоса под заголовком:
        там и причина, и что будет с отправленным, и номер попытки.
        Дублировать её здесь было незачем.

        Само состояние при этом остаётся читаемым: подсказка по наведению и
        `aria-label` — тому, кто полосу не видит вовсе.
      */}
      {/*
        «Почта» — как в Discord: справа в полосе окна, а не в ряду серверов. Окно
        «Почты» встаёт прямо под этим значком (`data-inbox-anchor`).
      */}
      {inbox && (
        <Hint label="Почта — Ctrl+I" side="bottom">
          <button
            type="button"
            className="titlebar-action no-drag"
            data-inbox-anchor=""
            aria-label={inbox.count > 0 ? `Почта, упоминаний ${inbox.count}` : "Почта"}
            onClick={inbox.open}
          >
            <IconInbox size={16} />
            {inbox.count > 0 && <span className="titlebar-badge">{inbox.count > 99 ? "99+" : inbox.count}</span>}
          </button>
        </Hint>
      )}

      {/* Справка — как в Discord, рядом с «Почтой». Пока заглушка: справки ещё нет. */}
      {inbox && (
        <Hint label="Справка — скоро" side="bottom">
          <button type="button" className="titlebar-action no-drag" aria-label="Справка" aria-disabled>
            <IconHelp size={16} />
          </button>
        </Hint>
      )}

      {status && (
        <Hint label={status.label} side="bottom">
          <span
            className={`titlebar-status ${status.bad ? "bad" : ""}`}
            role="status"
            aria-label={status.label}
          >
            <span className={`dot ${status.bad ? "bad" : ""}`} />
          </span>
        </Hint>
      )}

      {isDesktop() && (
        <div className="titlebar-buttons no-drag">
          <button type="button" onClick={() => void windowControls.minimize()} aria-label="Свернуть">
            <IconMinimize size={13} />
          </button>
          <button type="button" onClick={() => void windowControls.toggleMaximize()} aria-label="Развернуть">
            <IconMaximize size={12} />
          </button>
          <button type="button" className="close" onClick={() => void windowControls.close()} aria-label="Закрыть">
            <IconClose size={13} />
          </button>
        </div>
      )}
    </header>
  );
}
